import { useAuth } from "@tellescope/tanstack-query";
import { useMemo } from "react";

/**
 * Hook to get available Twilio phone numbers for the current user.
 *
 * Priority order:
 * 1. User's assigned numbers (availableFromNumbers) - if user has specific assigned numbers
 * 2. User's personal number (twilioNumber)
 * 3. Organization's main number (orgTwilioNumber)
 * 4. Organization's additional numbers (twilioNumbers)
 *
 * @returns Array of available phone numbers (deduplicated)
 */
export function useTwilioNumbers(): string[] {
  const { session, selectedOrganization: organization } = useAuth();

  return useMemo(() => {
    const userInfo = session?.userInfo;

    // If user has specific assigned numbers, use those
    if (userInfo?.availableFromNumbers?.length) {
      return userInfo.availableFromNumbers;
    }

    const numbers: string[] = [];

    // User's personal number
    if (userInfo?.twilioNumber) {
      numbers.push(userInfo.twilioNumber);
    }

    // Org's main number (from session.userInfo which contains orgTwilioNumber)
    if (userInfo?.orgTwilioNumber) {
      numbers.push(userInfo.orgTwilioNumber);
    }

    // Org's additional numbers
    if (organization?.twilioNumbers?.length) {
      numbers.push(...organization.twilioNumbers);
    }

    // Deduplicate and return
    return [...new Set(numbers)];
  }, [session?.userInfo, organization?.twilioNumbers]);
}

/**
 * Get a label for a phone number if one exists in org settings.
 *
 * @param phoneNumber - The phone number to get a label for
 * @returns The label if found, or the phone number itself
 */
export function usePhoneNumberLabel() {
  const { selectedOrganization: organization } = useAuth();

  return useMemo(() => {
    const labelMap = new Map<string, string>();
    organization?.phoneLabels?.forEach(({ number, label }) => {
      labelMap.set(number, label);
    });

    return (phoneNumber: string): string => {
      return labelMap.get(phoneNumber) || phoneNumber;
    };
  }, [organization?.phoneLabels]);
}
